import { BedIcon, StarIcon } from '@phosphor-icons/react';
import { useImageFallback } from '@voll-intelligence/ui-kit/hooks/use-image-fallback';
import { Popover, PopoverContent, PopoverTrigger } from '@voll-intelligence/ui-kit/ui/popover';
import {
  localizedBoardLabel,
  type HotelItemDetail,
  type CheckoutItem,
} from '@voll-intelligence/genui-contract';
import { fmtDayMonth } from '../../../../shared/ui/flight_format.js';
import {
  genUiT,
  resolveGenUiLocale,
  useGenUiI18n,
  type GenUiTranslate,
} from '../../../../shared/ui/i18n.js';
import {
  hotelCancellationLabel,
  staySummary,
  type CheckoutHotelCopy,
} from '@voll-intelligence/intelligence-ui-kit/checkout';
import { useCheckoutEnv } from '../../core/checkout_env.js';

/** O item de hotel do checkout no fio. */
export type HotelCheckoutItem = Extract<CheckoutItem, { productType: 'hotel' }>;

const MUTED = 'text-xs leading-4 text-muted-foreground';

function decimal(value: number, locale: string): string {
  return value.toLocaleString(resolveGenUiLocale(locale), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

/** «★ 4,6 · 3.218 avaliações»; sem nota (ausente ou 0) não há linha. */
function ratingView(detail: HotelItemDetail, t: GenUiTranslate, locale: string) {
  if (!detail.rating) return undefined;
  const value = decimal(detail.rating, locale);
  const { reviewCount } = detail;
  const reviews =
    reviewCount === undefined
      ? undefined
      : t(reviewCount === 1 ? 'hotelRooms.reviews_one' : 'hotelRooms.reviews_other', {
          count: reviewCount.toLocaleString(resolveGenUiLocale(locale)),
        });
  return {
    text: reviews ? `${value} · ${reviews}` : value,
    label: [t('hotel.rating', { rating: value }), reviews].filter(Boolean).join(' · '),
  };
}

/** Os fatos novos do bloco (#5281). Um card gravado antes deles não tem nenhum. */
function hasStayFacts(detail: HotelItemDetail): boolean {
  return (
    detail.rating !== undefined ||
    detail.reviewCount !== undefined ||
    detail.amenities !== undefined ||
    detail.distanceKm !== undefined ||
    detail.city !== undefined
  );
}

/** distância · endereço · cidade, numa linha (decisão do designer, 17/10). A
 *  distância nomeia até onde quando o agente sabe a âncora da busca. Card
 *  antigo, sem os fatos novos, segue com a legenda da estadia de antes. */
function locationLine(
  detail: HotelItemDetail,
  t: GenUiTranslate,
  locale: string,
  hotel: CheckoutHotelCopy,
): string {
  if (!hasStayFacts(detail)) return staySummary(detail, locale, hotel);
  const km =
    detail.distanceKm === undefined
      ? undefined
      : detail.distanceKm.toLocaleString(resolveGenUiLocale(locale), { maximumFractionDigits: 1 });
  const distance =
    km &&
    (detail.distanceAnchor
      ? t('productCheckout.hotel.distanceFrom', { km, place: detail.distanceAnchor })
      : t('hotel.card.distance', { km }));
  return [distance, detail.address, detail.city].filter(Boolean).join(' · ');
}

/** Comodidade com `code` sai no idioma da tela; sem `code`, o texto do fornecedor. */
function amenitiesLine(detail: HotelItemDetail, locale: string): string | undefined {
  const labels = (detail.amenities ?? []).map((amenity) =>
    amenity.code ? genUiT(locale, `hotel.amenity.${amenity.code}`) : amenity.label,
  );
  return labels.length > 0 ? labels.join(' · ') : undefined;
}

/** «pelas 3 noites»: a nota sob o total do rodapé. */
export function hotelNightsNote(item: HotelCheckoutItem, t: GenUiTranslate): string | undefined {
  const { nights } = item.detail;
  if (!nights) return undefined;
  const key =
    nights === 1
      ? 'productCheckout.hotel.nightsNote_one'
      : 'productCheckout.hotel.nightsNote_other';
  return t(key, { count: nights });
}

/** A ficha de «Detalhes»: o que saiu do bloco (as datas e as noites) e o nome do
 *  quarto como o fornecedor o escreve. Só leitura, então abre igual depois de
 *  pedir. */
function StayDetails({ detail }: { readonly detail: HotelItemDetail }) {
  const { t, locale } = useGenUiI18n();
  const { hotel } = useCheckoutEnv().copy;
  const cancellation = hotelCancellationLabel(detail, hotel, locale);
  const rows: [string, string][] = [];
  if (detail.checkIn)
    rows.push([t('hotelReservation.checkin'), fmtDayMonth(detail.checkIn, locale)]);
  if (detail.checkOut) {
    rows.push([t('hotelReservation.checkout'), fmtDayMonth(detail.checkOut, locale)]);
  }
  const nightsKey = detail.nights === 1 ? 'hotelRooms.nights_one' : 'hotelRooms.nights_other';
  return (
    <div className="flex flex-col gap-2 text-xs leading-4">
      {rows.length > 0 && (
        <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1">
          {rows.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="m-0 font-medium text-foreground">{v}</dd>
            </div>
          ))}
        </dl>
      )}
      {detail.nights && (
        <p className="m-0 text-muted-foreground">{t(nightsKey, { count: detail.nights })}</p>
      )}
      {cancellation && (
        <p className={`m-0 ${cancellation.charged ? 'text-destructive' : 'text-muted-foreground'}`}>
          {cancellation.text}
        </p>
      )}
      {detail.roomSource && <p className="m-0 text-muted-foreground">{detail.roomSource}</p>}
    </div>
  );
}

/** quarto · plano · cancelamento, e «Detalhes» na ponta. A condição de
 *  cancelamento fica à vista: é a última chance de vê-la antes de pedir (C3). */
function StayRuler({ detail }: { readonly detail: HotelItemDetail }) {
  const { t, locale } = useGenUiI18n();
  const { hotel } = useCheckoutEnv().copy;
  const cancellation = hotelCancellationLabel(detail, hotel, locale);
  const board = localizedBoardLabel(detail.board, locale);
  const parts: { key: string; text: string; tone: string }[] = [];
  if (detail.roomName) parts.push({ key: 'room', text: detail.roomName, tone: 'text-foreground' });
  if (board) parts.push({ key: 'board', text: board, tone: 'text-foreground' });
  if (cancellation) {
    parts.push({
      key: 'cancel',
      text: cancellation.text,
      tone: cancellation.charged ? 'text-destructive' : 'text-muted-foreground',
    });
  }
  const title = parts.map((part) => part.text).join(' · ');
  return (
    <div className="flex min-h-[18px] min-w-0 items-center gap-3 text-[13px] leading-[18px] font-medium">
      <span
        className="min-w-0 truncate @max-[theme(--breakpoint-sm)]/checkout:whitespace-normal"
        title={title || undefined}
      >
        {parts.map((part, index) => (
          <span key={part.key} className={part.tone}>
            {index > 0 && <span className="text-foreground"> · </span>}
            <span>{part.text}</span>
          </span>
        ))}
      </span>
      <Popover>
        <PopoverTrigger
          render={
            <button
              type="button"
              className="ml-auto shrink-0 cursor-pointer text-xs leading-4 font-medium text-muted-foreground underline underline-offset-2"
            >
              {t('productCheckout.hotel.details')}
            </button>
          }
        />
        {/* O portal monta fora de `.genui2`: a classe traz os tokens do DS. */}
        <PopoverContent className="genui2 w-64" align="end">
          <StayDetails detail={detail} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * O bloco «Hospedagem» do hotel no card 1click (#5281, Figma final 17/10): a
 * foto ao lado do nome e da nota; distância · endereço · cidade e as
 * comodidades embaixo; a régua quarto · plano com «Detalhes». Sem nota a
 * estrela some, sem comodidades a linha some. A rede do hotel não vem do
 * fornecedor (#5372), então o cabeçalho fica só «Hospedagem».
 */
export function HotelCheckoutStay({ item }: { readonly item: HotelCheckoutItem }) {
  const { t, locale } = useGenUiI18n();
  const { detail } = item;
  const photo = useImageFallback(detail.thumbUrl);
  const rating = ratingView(detail, t, locale);
  const location = locationLine(detail, t, locale, useCheckoutEnv().copy.hotel);
  const amenities = amenitiesLine(detail, locale);
  return (
    <div className="flex flex-col">
      <div className="flex h-6 min-w-0 items-center gap-1.5 text-xs leading-4">
        <BedIcon size={16} weight="bold" aria-hidden className="shrink-0 text-foreground" />
        <span className="font-semibold text-foreground">{t('productCheckout.hotel.head')}</span>
      </div>
      <div className="flex min-w-0 gap-4 pt-2 pb-[7px] @max-[theme(--breakpoint-sm)]/checkout:gap-3">
        <span
          aria-hidden
          className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-sand-3 text-sand-11 @max-[theme(--breakpoint-sm)]/checkout:size-16 @max-[theme(--breakpoint-sm)]/checkout:rounded-xl"
        >
          {photo.src ? (
            <img
              src={photo.src}
              alt=""
              onError={photo.onError}
              className="size-full object-cover"
            />
          ) : (
            <BedIcon size={28} />
          )}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex min-w-0 items-baseline gap-3 @max-[theme(--breakpoint-sm)]/checkout:flex-col @max-[theme(--breakpoint-sm)]/checkout:gap-0.5">
            <p
              className="m-0 min-w-0 flex-1 truncate text-[15px] leading-[22px] font-semibold text-foreground @max-[theme(--breakpoint-sm)]/checkout:w-full"
              title={detail.hotelName}
            >
              {detail.hotelName ?? t('hotel.room')}
            </p>
            {rating && (
              <span
                className={`inline-flex shrink-0 items-center gap-1 tabular-nums ${MUTED}`}
                aria-label={rating.label}
              >
                <StarIcon size={12} weight="fill" aria-hidden />
                <span aria-hidden>{rating.text}</span>
              </span>
            )}
          </div>
          {(location || amenities) && (
            <div className="flex min-w-0 flex-col gap-px text-xs leading-4">
              {location && (
                <p className="m-0 truncate text-muted-foreground" title={location}>
                  {location}
                </p>
              )}
              {amenities && (
                <p className="m-0 truncate text-sand-11" title={amenities}>
                  {amenities}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      <StayRuler detail={detail} />
    </div>
  );
}
